import * as React from 'react';
import {
  Tournament,
  TournamentCustomGroup,
  TournamentCompetitionDefinition,
  TournamentCompetitionRuntime,
  TournamentLayout
} from 'Src/models/tournament';
import { SchoolMark } from './TournamentStructureWorkspace';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { structureId } from './structureId';
import {
  SUBSTAGES_OF_TOURNAMENT_SERVER_TO_CLIENT_MAPPING,
  TOURNAMENT_PRIZES_SERVER_TO_CLIENT_MAPPING
} from 'Src/consts/tournament';

type Layout = NonNullable<TournamentCompetitionDefinition['layout']>;
interface Props {
  tournament: Tournament;
  runtime?: TournamentCompetitionRuntime;
  layout: Layout;
  stageId: string;
  update: (change: (layout: Layout) => Layout) => void;
  rankingEditor?: React.ReactNode;
  onCompetition?: (id: string) => void;
}
export function initialStructureLayout(tournament: Tournament, runtime?: TournamentCompetitionRuntime): Layout {
  return {
    stages: [...(tournament.tournamentLayout || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    groups: [...(tournament.customGroups || [])],
    assignments: (tournament.customGroups || [])
      .map(group => ({
        groupId: group.id,
        teamIds:
          tournament.roundRobinTeamOrders
            ?.find(item => item.groupId === group.id)
            ?.teamIds.filter(id => runtime?.teams.some(team => team.id === id && team.groupIds.includes(group.id))) ||
          []
      }))
      .map(item => ({
        ...item,
        teamIds: [
          ...item.teamIds,
          ...(runtime?.teams || [])
            .filter(team => team.groupIds.includes(item.groupId) && !item.teamIds.includes(team.id))
            .map(team => team.id)
        ]
      }))
  };
}

export const TournamentStructureGroupsBoard: React.FC<Props> = ({
  tournament,
  runtime,
  layout,
  stageId,
  update,
  rankingEditor,
  onCompetition
}) => {
  const [query, setQuery] = React.useState('');
  const [newName, setNewName] = React.useState('');
  const [autoFill, setAutoFill] = React.useState(false);
  const [redraw, setRedraw] = React.useState(false);
  const [mode, setMode] = React.useState('order');
  const [clear, setClear] = React.useState(false);
  const [deleteStage, setDeleteStage] = React.useState(false);
  const [deleteGroupId, setDeleteGroupId] = React.useState<string>();
  const [error, setError] = React.useState('');
  const groups = layout.groups.filter(group => group.stageId === stageId);
  const stage = layout.stages.find(item => item.id === stageId);
  if (!stage) return null;
  const assigned = (groupId: string) => layout.assignments.find(item => item.groupId === groupId)?.teamIds || [];
  const stageAssigned = groups.reduce((all, group) => all.concat(assigned(group.id)), [] as string[]);
  const unassigned = (runtime?.teams || []).filter(team => !stageAssigned.includes(team.id));
  const identity = (id: string) => {
    const team = runtime?.teams.find(item => item.id === id);
    const school = tournament.schools?.find(item => item.id === team?.schoolId);
    const schoolName = school?.name || team?.name || 'Tournament team';
    const multiple = (runtime?.teams || []).filter(item => item.schoolId === team?.schoolId).length > 1;
    return {
      name: `${schoolName}${multiple && team?.name ? ` · ${team.name}` : ''}`,
      schoolName,
      pic: school?.pic,
      region: (school as any)?.region || ''
    };
  };
  const moveTeam = (teamId: string, to?: string) =>
    update(current => ({
      ...current,
      assignments: current.groups.map(group => ({
        groupId: group.id,
        teamIds:
          group.stageId === stageId
            ? [
                ...(current.assignments.find(item => item.groupId === group.id)?.teamIds || []).filter(
                  id => id !== teamId
                ),
                ...(group.id === to ? [teamId] : [])
              ]
            : current.assignments.find(item => item.groupId === group.id)?.teamIds || []
      }))
    }));
  const reorder = (groupId: string, index: number, direction: -1 | 1) =>
    update(current => ({
      ...current,
      assignments: current.assignments.map(item => {
        if (item.groupId !== groupId) return item;
        const ids = [...item.teamIds];
        [ids[index], ids[index + direction]] = [ids[index + direction], ids[index]];
        return { ...item, teamIds: ids };
      })
    }));
  const addGroups = (names: string[]) => {
    if (names.some(name => groups.some(group => group.name.trim().toLowerCase() === name.trim().toLowerCase()))) {
      setError('A competition with this name already exists in this stage.');
      return;
    }
    update(current => ({
      ...current,
      groups: [...current.groups, ...names.map(name => ({ id: structureId(), name, stageId }))]
    }));
    setNewName('');
    setError('');
  };
  const fillGroups = () => {
    if (!groups.length) return;
    const candidates = [...(redraw ? runtime?.teams || [] : unassigned)];
    if (mode !== 'order')
      for (let i = candidates.length - 1; i > 0; i--) {
        const other = Math.floor(Math.random() * (i + 1));
        [candidates[i], candidates[other]] = [candidates[other], candidates[i]];
      }
    const result = groups.map(group => ({ groupId: group.id, teamIds: redraw ? [] : [...assigned(group.id)] }));
    candidates.forEach(team => {
      const smallest = Math.min(...result.map(item => item.teamIds.length));
      const options = result.filter(item => item.teamIds.length === smallest);
      const region = identity(team.id).region;
      const destination =
        mode === 'regions' && region
          ? options.find(item => !item.teamIds.some(id => identity(id).region === region)) || options[0]
          : options[0];
      destination.teamIds.push(team.id);
    });
    update(current => ({
      ...current,
      assignments: [...current.assignments.filter(item => !groups.some(group => group.id === item.groupId)), ...result]
    }));
    setAutoFill(false);
  };
  const changeStage = (patch: Partial<TournamentLayout>) =>
    update(current => ({
      ...current,
      stages: current.stages.map(item => (item.id === stageId ? { ...item, ...patch } : item))
    }));
  const moveStage = (direction: -1 | 1) =>
    update(current => {
      const stages = [...current.stages];
      const index = stages.findIndex(item => item.id === stageId);
      [stages[index], stages[index + direction]] = [stages[index + direction], stages[index]];
      return { ...current, stages: stages.map((item, order) => ({ ...item, order })) };
    });
  const patchGroup = (id: string, patch: Partial<TournamentCustomGroup>) =>
    update(current => ({
      ...current,
      groups: current.groups.map(group => (group.id === id ? { ...group, ...patch } : group))
    }));
  const groupSettings = (group: TournamentCustomGroup) => (
    <details className="tswGroupSettings">
      <summary>Group settings</summary>
      <label>
        Stage
        <select value={group.stageId} onChange={event => patchGroup(group.id, { stageId: event.target.value })}>
          {layout.stages.map(item => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Default pitch
        <select
          value={group.pitchMode === 'NONE' ? '__none' : group.pitchId || ''}
          onChange={event => {
            const value = event.target.value;
            patchGroup(group.id, {
              pitchId: value === '__none' ? undefined : value || undefined,
              pitchMode: value === '__none' ? 'NONE' : value ? 'SET' : 'AUTO'
            });
          }}
        >
          <option value="">Auto</option>
          <option value="__none">Not set</option>
          {tournament.pitches?.map(pitch => (
            <option key={pitch.id} value={pitch.id}>
              {pitch.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Prize
        <select
          value={group.prize || ''}
          onChange={event => patchGroup(group.id, { prize: (event.target.value as any) || undefined })}
        >
          <option value="">None</option>
          {Object.keys(TOURNAMENT_PRIZES_SERVER_TO_CLIENT_MAPPING).map(value => (
            <option key={value} value={value}>
              {TOURNAMENT_PRIZES_SERVER_TO_CLIENT_MAPPING[value]}
            </option>
          ))}
        </select>
      </label>
      <label>
        Substage
        <select
          value={group.substage || ''}
          onChange={event => patchGroup(group.id, { substage: (event.target.value as any) || undefined })}
        >
          <option value="">None</option>
          {Object.keys(SUBSTAGES_OF_TOURNAMENT_SERVER_TO_CLIENT_MAPPING).map(value => (
            <option key={value} value={value}>
              {SUBSTAGES_OF_TOURNAMENT_SERVER_TO_CLIENT_MAPPING[value]}
            </option>
          ))}
        </select>
      </label>
      {(group.roundRobinRounds || 1) > 1 && (
        <p>{group.roundRobinRounds} rounds · use the existing group schedule in Fixtures.</p>
      )}
      <button type="button" onClick={() => setDeleteGroupId(group.id)}>
        Delete competition…
      </button>
    </details>
  );
  return (
    <div className="tswGroupsBoard">
      <div className="tswBoardToolbar">
        <span>Stage {layout.stages.findIndex(item => item.id === stageId) + 1}</span>
        <div>
          <button type="button" disabled={layout.stages[0]?.id === stageId} onClick={() => moveStage(-1)}>
            Move earlier
          </button>
          <button
            type="button"
            disabled={layout.stages[layout.stages.length - 1]?.id === stageId}
            onClick={() => moveStage(1)}
          >
            Move later
          </button>
        </div>
      </div>
      <div className="tswStageFields">
        <label>
          <span>Stage name</span>
          <input
            aria-label="Stage name"
            value={stage.name}
            onChange={event => changeStage({ name: event.target.value })}
          />
        </label>
        <label>
          <span>Format</span>
          <select
            value={
              stage.format ||
              (stage.type === 'GROUP' || groups.some(group => runtime?.groups[group.id]) ? 'ROUND_ROBIN' : 'KNOCKOUT')
            }
            onChange={event => {
              const format = event.target.value as 'ROUND_ROBIN' | 'KNOCKOUT';
              changeStage({
                format,
                type: format === 'ROUND_ROBIN' ? (layout.stages[0]?.id === stageId ? 'GROUP' : 'QUALIFIER') : 'FINAL'
              });
            }}
          >
            <option value="ROUND_ROBIN">Round robin</option>
            <option value="KNOCKOUT">Knockout</option>
          </select>
        </label>
      </div>
      <label className="tswStageStart">
        <span>Stage starts</span>
        <input
          type="time"
          aria-label="Stage start time"
          value={new Date(stage.startTime || tournament.startTime).toTimeString().slice(0, 5)}
          onChange={event => {
            if (!event.target.value) return;
            const date = new Date(stage.startTime || tournament.startTime);
            const [hours, minutes] = event.target.value.split(':').map(Number);
            date.setHours(hours, minutes, 0, 0);
            changeStage({ startTime: date.toISOString() });
          }}
        />
      </label>
      {layout.stages.findIndex(item => item.id === stageId) > 0 &&
        +new Date(stage.startTime || tournament.startTime) <
          +new Date(
            layout.stages[layout.stages.findIndex(item => item.id === stageId) - 1].startTime || tournament.startTime
          ) && (
          <p className="tswSourceWarnings" role="alert">
            This stage starts before the previous stage. Review its start time.
          </p>
        )}
      {rankingEditor}
      <header className="tswBoardToolbar">
        <h3>{stage.type === 'GROUP' ? 'Groups & teams' : 'Competitions'}</h3>
        <div>
          {stage.type === 'GROUP' && (
            <>
              <button type="button" disabled={!groups.length || !runtime} onClick={() => setAutoFill(true)}>
                Auto-fill groups
              </button>
              <button type="button" disabled={!stageAssigned.length} onClick={() => setClear(true)}>
                Clear assignments
              </button>
            </>
          )}
          <button type="button" onClick={() => setDeleteStage(true)}>
            Delete stage…
          </button>
        </div>
      </header>
      {stage.type !== 'GROUP' && (
        <p>Configure finishing places and knockout sources by selecting a competition in the stage tree.</p>
      )}
      {stage.type === 'GROUP' ? (
        <div className="tswBoardGrid">
          <aside
            className="tswUnassigned"
            onDragOver={event => event.preventDefault()}
            onDrop={event => {
              event.preventDefault();
              const id = event.dataTransfer.getData('application/x-tournament-team');
              if (runtime?.teams.some(team => team.id === id)) moveTeam(id);
            }}
          >
            <h3>
              Unassigned <span>{unassigned.length}</span>
            </h3>
            <input
              type="search"
              placeholder="Find a team"
              aria-label="Find an unassigned team"
              value={query}
              onChange={event => setQuery(event.target.value)}
            />
            {!runtime && <p>Loading tournament teams…</p>}
            {unassigned
              .filter(team =>
                identity(team.id)
                  .name.toLowerCase()
                  .includes(query.toLowerCase())
              )
              .map(team => {
                const school = identity(team.id);
                return (
                  <div
                    className="tswTeam"
                    key={team.id}
                    draggable
                    onDragStart={event => event.dataTransfer.setData('application/x-tournament-team', team.id)}
                  >
                    <SchoolMark name={school.schoolName} pic={school.pic} />
                    <span>
                      {school.name}
                      {school.region && <small>{school.region}</small>}
                    </span>
                    <select
                      aria-label={`Assign ${school.name}`}
                      value=""
                      onChange={event => moveTeam(team.id, event.target.value)}
                    >
                      <option value="">Assign…</option>
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
          </aside>
          <div className="tswGroupCards">
            {groups.map(group => (
              <article
                key={group.id}
                onDragOver={event => event.preventDefault()}
                onDrop={event => {
                  event.preventDefault();
                  const id = event.dataTransfer.getData('application/x-tournament-team');
                  if (runtime?.teams.some(team => team.id === id)) moveTeam(id, group.id);
                }}
              >
                <header>
                  <input
                    aria-label={`Competition name ${group.name}`}
                    value={group.name}
                    onChange={event =>
                      update(current => ({
                        ...current,
                        groups: current.groups.map(item =>
                          item.id === group.id ? { ...item, name: event.target.value } : item
                        )
                      }))
                    }
                  />
                  <span>{assigned(group.id).length} teams</span>
                  <button
                    type="button"
                    aria-label={`Delete empty ${group.name}`}
                    disabled={!!assigned(group.id).length || !!runtime?.groups[group.id]?.completedMatches}
                    onClick={() => setDeleteGroupId(group.id)}
                  >
                    ×
                  </button>
                </header>
                {assigned(group.id).map((teamId, index, ids) => {
                  const school = identity(teamId);
                  const clash = school.region && ids.filter(id => identity(id).region === school.region).length > 1;
                  return (
                    <div
                      className="tswTeam"
                      key={teamId}
                      draggable
                      onDragStart={event => event.dataTransfer.setData('application/x-tournament-team', teamId)}
                    >
                      <SchoolMark name={school.schoolName} pic={school.pic} />
                      <span>
                        {school.name}
                        {school.region && (
                          <small className={clash ? 'is-warning' : ''}>
                            {school.region}
                            {clash ? ' · region shared' : ''}
                          </small>
                        )}
                      </span>
                      <div className="tswTeam__actions">
                        <button
                          type="button"
                          aria-label={`Move ${school.name} up`}
                          disabled={!index}
                          onClick={() => reorder(group.id, index, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          aria-label={`Move ${school.name} down`}
                          disabled={index === ids.length - 1}
                          onClick={() => reorder(group.id, index, 1)}
                        >
                          ↓
                        </button>
                        <button type="button" aria-label={`Unassign ${school.name}`} onClick={() => moveTeam(teamId)}>
                          ×
                        </button>
                      </div>
                    </div>
                  );
                })}
                {!assigned(group.id).length && <p className="tswDropHint">Drop teams here or use Assign.</p>}
                {groupSettings(group)}
              </article>
            ))}
          </div>
        </div>
      ) : (
        <div className="tswCompetitionCards">
          {groups.map(group => (
            <article key={group.id}>
              <button type="button" onClick={() => onCompetition?.(group.id)}>
                <strong>{group.name}</strong>
                <span>Configure entrants and fixtures →</span>
              </button>
              {groupSettings(group)}
            </article>
          ))}
        </div>
      )}
      <form
        className="tswQuickGroup"
        onSubmit={event => {
          event.preventDefault();
          if (newName.trim()) addGroups([newName.trim()]);
        }}
      >
        <input
          aria-label="New group name"
          placeholder="New group name"
          value={newName}
          onChange={event => setNewName(event.target.value)}
        />
        <button type="submit" disabled={!newName.trim()}>
          Add group
        </button>
        {[4, 8].map(count => (
          <button
            type="button"
            key={count}
            onClick={() =>
              addGroups(
                Array.from({ length: count }, (_, index) => `Group ${String.fromCharCode(65 + groups.length + index)}`)
              )
            }
          >
            + {count} groups
          </button>
        ))}
      </form>
      {error && <p role="alert">{error}</p>}
      <SimpleModal
        isOpen={!!deleteGroupId}
        title={`Delete ${groups.find(group => group.id === deleteGroupId)?.name || 'competition'}?`}
        showFooter={false}
        customClass="tcsChoiceModal"
        onCloseClick={() => setDeleteGroupId(undefined)}
      >
        <div className="tswModalContent">
          <p>
            Its teams return to the unassigned pool. References from other competitions stay in the draft for review.
            Affected fixtures and results will be listed for your approval before publication.
          </p>
          <footer>
            <button type="button" onClick={() => setDeleteGroupId(undefined)}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                update(current => ({
                  ...current,
                  groups: current.groups.filter(group => group.id !== deleteGroupId),
                  assignments: current.assignments.filter(item => item.groupId !== deleteGroupId)
                }));
                setDeleteGroupId(undefined);
              }}
            >
              Delete competition
            </button>
          </footer>
        </div>
      </SimpleModal>
      <SimpleModal
        isOpen={deleteStage}
        title={`Delete ${stage.name}?`}
        showFooter={false}
        customClass="tcsChoiceModal"
        onCloseClick={() => setDeleteStage(false)}
      >
        <div className="tswModalContent">
          <p>
            The stage and its competitions will be removed from the draft. References from other stages will be retained
            and must be reviewed before publication. Affected fixtures and results will be listed for your approval
            before publication.
          </p>
          <footer>
            <button type="button" onClick={() => setDeleteStage(false)}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                update(current => ({
                  ...current,
                  stages: current.stages.filter(item => item.id !== stageId),
                  groups: current.groups.filter(item => item.stageId !== stageId),
                  assignments: current.assignments.filter(item => !groups.some(group => group.id === item.groupId))
                }));
                setDeleteStage(false);
              }}
            >
              Delete stage
            </button>
          </footer>
        </div>
      </SimpleModal>
      <SimpleModal
        isOpen={autoFill}
        title="Auto-fill groups"
        showFooter={false}
        customClass="tcsChoiceModal"
        onCloseClick={() => setAutoFill(false)}
      >
        <div className="tswModalContent">
          <p>Distribute teams evenly across this stage. Changes stay in the draft until publication.</p>
          {[
            ['order', 'List order'],
            ['random', 'Random draw'],
            ['regions', 'Keep regions apart where possible']
          ].map(([value, label]) => (
            <label key={value}>
              <input
                type="radio"
                name="fill-mode"
                value={value}
                checked={mode === value}
                onChange={() => setMode(value)}
              />
              {label}
            </label>
          ))}
          <label>
            <input type="checkbox" checked={redraw} onChange={event => setRedraw(event.target.checked)} />
            Redraw this stage, including assigned teams
          </label>
          <footer>
            <button type="button" onClick={() => setAutoFill(false)}>
              Cancel
            </button>
            <button type="button" onClick={fillGroups}>
              Fill groups
            </button>
          </footer>
        </div>
      </SimpleModal>
      <SimpleModal
        isOpen={clear}
        title="Clear assignments in this stage?"
        showFooter={false}
        customClass="tcsChoiceModal"
        onCloseClick={() => setClear(false)}
      >
        <div className="tswModalContent">
          <p>All teams in this stage return to the unassigned pool. Other stages are unchanged.</p>
          <footer>
            <button type="button" onClick={() => setClear(false)}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                update(current => ({
                  ...current,
                  assignments: current.assignments.map(item =>
                    groups.some(group => group.id === item.groupId) ? { ...item, teamIds: [] } : item
                  )
                }));
                setClear(false);
              }}
            >
              Clear assignments
            </button>
          </footer>
        </div>
      </SimpleModal>
    </div>
  );
};
